import { Container, Sprite, Assets, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';
import { BaseScene } from './BaseScene';
import { UIButton } from '../components/UIButton';
import { LobbyOverlay } from '../components/LobbyOverlay';
import { COLORS, FONTS, SPACING } from '../constants/theme';
import { LAYOUT_STYLES } from '../constants/layoutStyles';
import { getPeerManager } from '@network/PeerManager';
import type { LayoutMetrics } from '../LayoutManager';

export interface RoomSceneOptions {
  onConnected: (isHost: boolean) => void;
  onBack: () => void;
}

/**
 * Room creation/joining scene for online multiplayer
 */
export class RoomScene extends BaseScene {
  private metrics: LayoutMetrics;
  private options: RoomSceneOptions;

  // Background
  private backgroundSprite: Sprite | null = null;

  // Root layout
  private rootLayout: Container | null = null;

  // Room code input (DOM element)
  private joinRoomInputContainer: HTMLDivElement | null = null;
  private joinRoomInput: HTMLInputElement | null = null;
  private joinRoomInputPlaceholder: Container | null = null;

  // Lobby overlay
  private lobbyOverlay: LobbyOverlay | null = null;

  // Buttons
  private backButton: UIButton | null = null;

  constructor(metrics: LayoutMetrics, options: RoomSceneOptions) {
    super();
    this.metrics = metrics;
    this.options = options;
    this.build();
  }

  protected build(): void {
    // Empty - content built in onEnter after assets load
  }

  private async loadAssets(): Promise<void> {
    try {
      const backgroundTexture = await Assets.load('/assets/background.png');

      this.backgroundSprite = new Sprite(backgroundTexture);
      this.backgroundSprite.anchor.set(0, 0);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  }

  private buildContent(): void {
    this.cleanupContent();

    const { width, height } = this.metrics;

    // Background
    if (this.backgroundSprite) {
      this.backgroundSprite.width = width;
      this.backgroundSprite.height = height;
      this.container.addChild(this.backgroundSprite);
    }

    // Root layout - centered column
    this.rootLayout = new Container({
      layout: {
        ...LAYOUT_STYLES.centeredScreen(width, height),
        flexDirection: 'column',
        gap: SPACING.LG,
      },
    });
    this.container.addChild(this.rootLayout);

    // Title text
    const titleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: Math.min(48, width * 0.08),
      fontWeight: 'bold',
      fill: COLORS.TEXT_PRIMARY,
    });
    const title = new Text({ text: 'Online Multiplayer', style: titleStyle });
    title.layout = true;
    this.rootLayout.addChild(title);

    // Subtitle
    const subtitleStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: Math.min(20, width * 0.04),
      fill: COLORS.TEXT_SECONDARY,
    });
    const subtitle = new Text({ text: 'Create or join a room to play online', style: subtitleStyle });
    subtitle.layout = true;
    this.rootLayout.addChild(subtitle);

    // Spacer
    const spacer = new Container({ layout: { height: SPACING.XL } });
    this.rootLayout.addChild(spacer);

    // Create Room button
    const buttonWidth = Math.min(280, width * 0.6);
    const buttonHeight = 60;

    const createWrapper = new Container({ layout: { width: buttonWidth, height: buttonHeight } });
    const createBtn = new UIButton({
      text: 'Create Room',
      width: buttonWidth,
      height: buttonHeight,
      variant: 'primary',
      onClick: () => this.handleCreateRoom(),
    });
    createBtn.x = buttonWidth / 2;
    createBtn.y = buttonHeight / 2;
    createWrapper.addChild(createBtn);
    this.rootLayout.addChild(createWrapper);

    // Divider text
    const orStyle = new TextStyle({
      fontFamily: FONTS.PRIMARY,
      fontSize: 16,
      fill: COLORS.TEXT_SECONDARY,
    });
    const orText = new Text({ text: '- OR -', style: orStyle });
    orText.layout = true;
    this.rootLayout.addChild(orText);

    // Join Room row
    const joinRowWidth = buttonWidth;
    const inputWidth = 150;
    const joinBtnWidth = 100;
    const rowGap = SPACING.SM;

    const joinRow = new Container({
      layout: {
        width: joinRowWidth,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: rowGap,
      },
    });
    this.rootLayout.addChild(joinRow);

    // Input placeholder (DOM input will be positioned over this)
    this.joinRoomInputPlaceholder = new Container({
      layout: { width: inputWidth, height: 48 },
    });
    joinRow.addChild(this.joinRoomInputPlaceholder);

    // Join button
    const joinBtnWrapper = new Container({ layout: { width: joinBtnWidth, height: 48 } });
    const joinBtn = new UIButton({
      text: 'Join',
      width: joinBtnWidth,
      variant: 'secondary',
      onClick: () => this.handleJoinRoom(),
    });
    joinBtn.x = joinBtnWidth / 2;
    joinBtn.y = 24;
    joinBtnWrapper.addChild(joinBtn);
    joinRow.addChild(joinBtnWrapper);

    // Spacer
    const spacer2 = new Container({ layout: { height: SPACING.XL } });
    this.rootLayout.addChild(spacer2);

    // Back button
    const backWidth = 120;
    const backWrapper = new Container({ layout: { width: backWidth, height: 48 } });
    this.backButton = new UIButton({
      text: 'Back',
      width: backWidth,
      variant: 'secondary',
      onClick: () => this.handleBack(),
    });
    this.backButton.x = backWidth / 2;
    this.backButton.y = 24;
    backWrapper.addChild(this.backButton);
    this.rootLayout.addChild(backWrapper);

    // Create DOM input after layout settles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.createJoinRoomInput();
      });
    });
  }

  private createJoinRoomInput(): void {
    if (!this.joinRoomInputPlaceholder) return;

    // Remove existing input if any
    if (this.joinRoomInputContainer) {
      this.joinRoomInputContainer.remove();
      this.joinRoomInputContainer = null;
      this.joinRoomInput = null;
    }

    const placeholderPos = this.joinRoomInputPlaceholder.toGlobal({ x: 0, y: 0 });

    const inputWidth = 150;
    const inputHeight = 48;

    this.joinRoomInputContainer = document.createElement('div');
    this.joinRoomInputContainer.style.cssText = `
      position: fixed;
      left: ${placeholderPos.x}px;
      top: ${placeholderPos.y}px;
      z-index: 1000;
    `;

    this.joinRoomInput = document.createElement('input');
    this.joinRoomInput.type = 'text';
    this.joinRoomInput.placeholder = 'Room Code';
    this.joinRoomInput.maxLength = 6;
    this.joinRoomInput.style.cssText = `
      width: ${inputWidth}px;
      height: ${inputHeight}px;
      font-family: monospace;
      font-size: 16px;
      font-weight: bold;
      text-align: center;
      text-transform: uppercase;
      background: #16213e;
      border: 2px solid #${COLORS.TEXT_SECONDARY.toString(16).padStart(6, '0')};
      border-radius: 4px;
      color: white;
      outline: none;
      box-sizing: border-box;
    `;

    this.joinRoomInput.addEventListener('input', () => {
      if (this.joinRoomInput) {
        this.joinRoomInput.value = this.joinRoomInput.value.toUpperCase();
      }
    });

    this.joinRoomInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleJoinRoom();
      }
    });

    this.joinRoomInputContainer.appendChild(this.joinRoomInput);
    document.body.appendChild(this.joinRoomInputContainer);
  }

  private cleanupContent(): void {
    // Remove join room input
    if (this.joinRoomInputContainer) {
      this.joinRoomInputContainer.remove();
      this.joinRoomInputContainer = null;
      this.joinRoomInput = null;
    }

    this.container.removeChildren();
    this.rootLayout = null;
    this.joinRoomInputPlaceholder = null;
    this.backButton = null;
  }

  private async handleCreateRoom(): Promise<void> {
    // Hide DOM input while overlay is visible
    if (this.joinRoomInputContainer) {
      this.joinRoomInputContainer.style.display = 'none';
    }

    this.lobbyOverlay = new LobbyOverlay(this.metrics, {
      mode: 'create',
      onCancel: () => {
        this.lobbyOverlay = null;
        getPeerManager().disconnect();
        // Show DOM input again
        if (this.joinRoomInputContainer) {
          this.joinRoomInputContainer.style.display = '';
        }
      },
    });
    this.container.addChild(this.lobbyOverlay);

    try {
      const roomCode = await getPeerManager().createRoom();
      if (this.lobbyOverlay) {
        this.lobbyOverlay.setRoomCode(roomCode);
        this.lobbyOverlay.setConnecting(true);
      }

      getPeerManager().setHandlers({
        onConnected: () => {
          if (this.lobbyOverlay) {
            this.lobbyOverlay.hide();
            this.lobbyOverlay = null;
          }
          this.cleanupContent();
          this.options.onConnected(true);
        },
        onError: (error) => {
          console.error('Connection error:', error);
          if (this.lobbyOverlay) {
            this.lobbyOverlay.showError('Connection failed');
          }
        },
      });
    } catch (error) {
      console.error('Failed to create room:', error);
      if (this.lobbyOverlay) {
        this.lobbyOverlay.showError('Failed to create room');
      }
    }
  }

  private async handleJoinRoom(): Promise<void> {
    if (!this.joinRoomInput) return;

    const roomCode = this.joinRoomInput.value.trim().toUpperCase();
    if (roomCode.length < 4) return;

    // Hide DOM input while overlay is visible
    if (this.joinRoomInputContainer) {
      this.joinRoomInputContainer.style.display = 'none';
    }

    // Show a joining overlay (no input needed - we already have the code)
    this.lobbyOverlay = new LobbyOverlay(this.metrics, {
      mode: 'joining',
      onCancel: () => {
        this.lobbyOverlay = null;
        getPeerManager().disconnect();
        // Show DOM input again
        if (this.joinRoomInputContainer) {
          this.joinRoomInputContainer.style.display = '';
        }
      },
    });
    this.container.addChild(this.lobbyOverlay);

    try {
      await getPeerManager().joinRoom(roomCode);
      if (this.lobbyOverlay) {
        this.lobbyOverlay.hide();
        this.lobbyOverlay = null;
      }
      this.cleanupContent();
      this.options.onConnected(false);
    } catch (error) {
      console.error('Failed to join room:', error);
      if (this.lobbyOverlay) {
        this.lobbyOverlay.showError('Failed to join room');
      }
    }
  }

  private handleBack(): void {
    this.cleanupContent();
    this.options.onBack();
  }

  async onEnter(): Promise<void> {
    super.onEnter();

    await this.loadAssets();
    this.buildContent();

    this.container.alpha = 0;
    gsap.to(this.container, { alpha: 1, duration: 0.3 });
  }

  onExit(): void {
    super.onExit();
    this.cleanupContent();
    if (this.lobbyOverlay) {
      this.lobbyOverlay.hide();
      this.lobbyOverlay = null;
    }
  }

  onResize(metrics: LayoutMetrics): void {
    this.metrics = metrics;
    if (this.isActive) {
      // Save overlay reference - buildContent will remove it from container
      const overlay = this.lobbyOverlay;
      this.buildContent();
      // Re-add overlay after rebuilding content
      if (overlay) {
        this.container.addChild(overlay);
        overlay.onResize(metrics);
      }
    }
  }

  dispose(): void {
    this.cleanupContent();
    if (this.lobbyOverlay) {
      this.lobbyOverlay.hide();
      this.lobbyOverlay = null;
    }
    super.dispose();
  }
}
